import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { buildRows, recalcDates, fmtD } from './planUtils';
import {
  getTiers, getZoomLabel, unitBoundaries, topTierLabel, middleTierLabel,
  PROJECT_DETAIL_PRESETS as PRESETS,
} from './zoomUtils';
import './GanttView.css';

// PL-S4: getTiers / getZoomLabel re-exported so existing importers
// (e.g. GanttView.zoom.test.js) keep importing them from this module.
export { getTiers, getZoomLabel } from './zoomUtils';

const BAR_HEIGHT = 22;
const DEFAULT_ROW_HEIGHT = 32;
const HEADER_HEIGHT = 56;
const TOP_TIER_HEIGHT = 28;
const MIDDLE_TIER_HEIGHT = 28;
// G-S4: zoom range now reaches down to 1 px/day to support Year preset (2 px)
const MIN_PX = 1;
const MAX_PX = 120;

// G-S2 column configuration
const DEFAULT_COLUMNS = { wbs: 50, mode: 40, name: 240, dur: 60, start: 100, finish: 100 };
const MIN_COL_WIDTH = 30;
const COLUMN_DEFS = [
  { key: 'wbs', label: '#' },
  { key: 'mode', label: 'Mode' },
  { key: 'name', label: 'Task Name' },
  { key: 'dur', label: 'Dur' },
  { key: 'start', label: 'Start' },
  { key: 'finish', label: 'Finish' },
];

// Splitter — task pane width bounds (G-S3)
const DEFAULT_TASK_PANE_WIDTH = 480;
const MIN_TASK_PANE_WIDTH = 150;
const MAX_TASK_PANE_RATIO = 0.8;

const barColor = (row) => {
  if (row.isSummaryDerived) return '#455a64';
  if (row.isMilestone) return '#e65100';
  switch (row.status) {
    case 'Completed': return '#43a047';
    case 'InProgress': return '#1976d2';
    case 'OnHold': return '#fb8c00';
    case 'Cancelled': return '#bdbdbd';
    default: return '#1976d2';
  }
};

const getProgress = (row) => {
  if (row.status === 'Completed') return 1.0;
  if (row.status === 'InProgress') return 0.5;
  return 0;
};

const isRowVisible = (rows, i, collapsed) => {
  let lv = rows[i].outlineLevel;
  for (let j = i - 1; j >= 0; j--) {
    if (rows[j].outlineLevel < lv) {
      if (rows[j].isSummaryDerived && collapsed.has(rows[j].id)) return false;
      lv = rows[j].outlineLevel;
      if (lv === 0) break;
    }
  }
  return true;
};

const readStored = (key, fallback) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored == null) return fallback;
    return JSON.parse(stored);
  } catch {
    return fallback;
  }
};

const writeStored = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

// G-S5: virtualization
const VIRTUALIZATION_THRESHOLD = 100;
const VIRTUALIZATION_BUFFER = 20;

// VW-S3: collapse state is owned by the parent (ProjectDetailPage) so the
// Effort and Gantt views share one set. Stable default keeps the props
// optional for stand-alone test renders.
const EMPTY_COLLAPSED = new Set();
const NOOP = () => {};

const GanttView = ({
  project, roles = [], tasks = [],
  collapsed = EMPTY_COLLAPSED,
  onToggleCollapse = NOOP,
  onExpandAll = NOOP,
  onCollapseAll = NOOP,
  // NF-S4 — holiday Set (YYYY-MM-DD strings) threaded from the parent.
  // Undefined → planUtils falls back to weekend-only date math.
  holidays,
}) => {
  const [pxPerDay, setPxPerDay] = useState(40);
  const [tooltip, setTooltip] = useState(null);
  // G-S5: cross-pane hover sync + viewport tracking for virtualization
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(0);
  const taskListRef = useRef(null);
  const timelineRef = useRef(null);
  const syncingRef = useRef(false);

  // ── G-S2: per-project persisted column widths ────────────────────────
  const columnsKey = `hrapp.gantt.layout.${project.id}.columns`;
  const splitterKey = `hrapp.gantt.layout.${project.id}.splitter`;

  const [colWidths, setColWidths] = useState(() => {
    const stored = readStored(columnsKey, null);
    return stored ? { ...DEFAULT_COLUMNS, ...stored } : DEFAULT_COLUMNS;
  });

  useEffect(() => {
    writeStored(columnsKey, colWidths);
  }, [colWidths, columnsKey]);

  // ── G-S2: task pane width state, used by splitter in G-S3 ─────────────
  const [taskListWidth, setTaskListWidth] = useState(() => {
    const stored = readStored(splitterKey, null);
    if (typeof stored === 'number' && !isNaN(stored)) return stored;
    return DEFAULT_TASK_PANE_WIDTH;
  });

  useEffect(() => {
    writeStored(splitterKey, taskListWidth);
  }, [taskListWidth, splitterKey]);

  // ── G-S3: splitter drag handler ───────────────────────────────────────
  const startSplitterDrag = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = taskListWidth;
    const maxW = (typeof window !== 'undefined' ? window.innerWidth : 1024) * MAX_TASK_PANE_RATIO;
    const onMove = (ev) => {
      const next = Math.min(maxW, Math.max(MIN_TASK_PANE_WIDTH, startW + (ev.clientX - startX)));
      setTaskListWidth(next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
    };
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [taskListWidth]);

  // ── G-S2: row height tracking ─────────────────────────────────────────
  const rowRefs = useRef([]);
  const [rowHeights, setRowHeights] = useState([]);

  const syncScroll = useCallback((source) => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    const src = source === 'list' ? taskListRef.current : timelineRef.current;
    const tgt = source === 'list' ? timelineRef.current : taskListRef.current;
    if (src && tgt) tgt.scrollTop = src.scrollTop;
    requestAnimationFrame(() => { syncingRef.current = false; });
  }, []);

  const allRows = useMemo(() => recalcDates(buildRows(tasks), holidays), [tasks, holidays]);

  const visibleRows = useMemo(
    () => allRows.filter((_, i) => isRowVisible(allRows, i, collapsed)),
    [allRows, collapsed]
  );

  rowRefs.current.length = visibleRows.length;

  useLayoutEffect(() => {
    const measured = visibleRows.map((_, i) => {
      const el = rowRefs.current[i];
      return el ? el.offsetHeight : DEFAULT_ROW_HEIGHT;
    });
    setRowHeights(prev => {
      if (prev.length === measured.length && prev.every((h, i) => h === measured[i])) return prev;
      return measured;
    });
  }, [visibleRows, colWidths]);

  // VW-S3: collapse is parent-controlled — chevron clicks delegate to the prop.
  const toggleCollapse = useCallback((rowId) => {
    onToggleCollapse(rowId);
  }, [onToggleCollapse]);

  // ── G-S2: column resize handler ──────────────────────────────────────
  const startColResize = useCallback((e, colKey) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[colKey];
    const onMove = (ev) => {
      const next = Math.max(MIN_COL_WIDTH, startW + (ev.clientX - startX));
      setColWidths(w => ({ ...w, [colKey]: next }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [colWidths]);

  const timelineStart = useMemo(() => {
    const ts = allRows.filter(r => r._start).map(r => r._start.getTime());
    if (!ts.length) return new Date();
    const d = new Date(Math.min(...ts));
    d.setDate(d.getDate() - 7);
    return d;
  }, [allRows]);

  const timelineEnd = useMemo(() => {
    const ts = allRows.filter(r => r._finish).map(r => r._finish.getTime());
    if (!ts.length) { const d = new Date(); d.setDate(d.getDate() + 60); return d; }
    const d = new Date(Math.max(...ts));
    d.setDate(d.getDate() + 14);
    return d;
  }, [allRows]);

  const totalDays = Math.ceil((timelineEnd - timelineStart) / 86400000);
  const totalWidth = Math.max(totalDays * pxPerDay, 200);

  // Variable-height row positioning (G-S2)
  const cumulativeYs = useMemo(() => {
    const arr = new Array(visibleRows.length);
    let y = HEADER_HEIGHT;
    for (let i = 0; i < visibleRows.length; i++) {
      arr[i] = y;
      y += rowHeights[i] || DEFAULT_ROW_HEIGHT;
    }
    return arr;
  }, [visibleRows.length, rowHeights]);

  const rowYOffset = useCallback((i) => cumulativeYs[i] ?? HEADER_HEIGHT, [cumulativeYs]);
  const rowH = useCallback((i) => rowHeights[i] || DEFAULT_ROW_HEIGHT, [rowHeights]);

  const totalHeight = useMemo(() => {
    let h = HEADER_HEIGHT;
    for (let i = 0; i < visibleRows.length; i++) h += rowH(i);
    return h;
  }, [visibleRows.length, rowH]);

  // ── G-S5: virtualization window ───────────────────────────────────────
  const virtualizationActive = visibleRows.length > VIRTUALIZATION_THRESHOLD;

  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;
    const update = () => {
      setScrollTop(el.scrollTop);
      setViewportH(el.clientHeight);
    };
    update();
    el.addEventListener('scroll', update);
    if (typeof window !== 'undefined') window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      if (typeof window !== 'undefined') window.removeEventListener('resize', update);
    };
  }, []);

  const { firstIdx, lastIdx } = useMemo(() => {
    if (!virtualizationActive) {
      return { firstIdx: 0, lastIdx: visibleRows.length - 1 };
    }
    // First row whose bottom > viewport top
    const viewportTop = scrollTop;
    const viewportBottom = scrollTop + viewportH;
    let first = 0;
    for (let i = 0; i < visibleRows.length; i++) {
      const yBottom = cumulativeYs[i] + (rowHeights[i] || DEFAULT_ROW_HEIGHT);
      if (yBottom > viewportTop) { first = i; break; }
      first = i + 1;
    }
    let last = Math.max(first, 0);
    for (let i = first; i < visibleRows.length; i++) {
      last = i;
      if (cumulativeYs[i] > viewportBottom) break;
    }
    return {
      firstIdx: Math.max(0, first - VIRTUALIZATION_BUFFER),
      lastIdx: Math.min(visibleRows.length - 1, last + VIRTUALIZATION_BUFFER),
    };
  }, [virtualizationActive, visibleRows.length, cumulativeYs, rowHeights, scrollTop, viewportH]);

  const topSpacerH = virtualizationActive && firstIdx > 0
    ? cumulativeYs[firstIdx] - HEADER_HEIGHT
    : 0;
  const bottomSpacerH = virtualizationActive && lastIdx < visibleRows.length - 1
    ? totalHeight - (cumulativeYs[lastIdx] + rowH(lastIdx))
    : 0;

  const dayToX = useCallback((date) => {
    return ((new Date(date).getTime() - timelineStart.getTime()) / 86400000) * pxPerDay;
  }, [timelineStart, pxPerDay]);

  // ── G-S4: zoom helpers ───────────────────────────────────────────────
  const tiers = useMemo(() => getTiers(pxPerDay), [pxPerDay]);
  const zoomLabel = useMemo(() => getZoomLabel(pxPerDay), [pxPerDay]);

  // Pin-point zoom: preserve the calendar date at the center of the viewport
  const zoomTo = useCallback((newPxPerDay) => {
    const clamped = Math.min(MAX_PX, Math.max(MIN_PX, newPxPerDay));
    const el = timelineRef.current;
    if (!el) {
      setPxPerDay(clamped);
      return;
    }
    const focalX = el.scrollLeft + el.clientWidth / 2;
    const focalDays = pxPerDay > 0 ? focalX / pxPerDay : 0;
    const newScrollLeft = focalDays * clamped - el.clientWidth / 2;
    setPxPerDay(clamped);
    requestAnimationFrame(() => {
      if (timelineRef.current) {
        timelineRef.current.scrollLeft = Math.max(0, newScrollLeft);
      }
    });
  }, [pxPerDay]);

  const fitToProject = useCallback(() => {
    const el = timelineRef.current;
    if (!el) return;
    const days = Math.max(1, (timelineEnd - timelineStart) / 86400000);
    const targetPx = Math.max(1, el.clientWidth / days);
    const clamped = Math.min(MAX_PX, Math.max(MIN_PX, targetPx));
    setPxPerDay(clamped);
    requestAnimationFrame(() => {
      if (timelineRef.current) timelineRef.current.scrollLeft = 0;
    });
  }, [timelineStart, timelineEnd]);

  const scrollToToday = useCallback(() => {
    const el = timelineRef.current;
    if (!el) return;
    const todayX = dayToX(new Date());
    el.scrollLeft = Math.max(0, todayX - el.clientWidth / 2);
  }, [dayToX]);

  const onBarEnter = useCallback((e, row, i) => {
    setTooltip({ x: e.clientX + 12, y: e.clientY + 12, row });
    setHoveredIdx(i);
  }, []);
  const onBarLeave = useCallback(() => {
    setTooltip(null);
  }, []);
  const onBarMove = useCallback((e) => setTooltip(t => t ? { ...t, x: e.clientX + 12, y: e.clientY + 12 } : null), []);


  // ── G-S4: multi-tier header segments ──────────────────────────────────
  const topTier = useMemo(() => {
    const raw = unitBoundaries(tiers.top, timelineStart, timelineEnd);
    return raw.map((seg, i) => {
      const x1 = dayToX(seg.start < timelineStart ? timelineStart : seg.start);
      const x2 = dayToX(seg.end > timelineEnd ? timelineEnd : seg.end);
      return {
        key: `t-${seg.start.getTime()}`,
        label: topTierLabel(tiers.top, seg.start),
        x: x1,
        width: Math.max(x2 - x1, 0),
        even: i % 2 === 0,
      };
    });
  }, [tiers.top, timelineStart, timelineEnd, dayToX]);

  const middleTier = useMemo(() => {
    const raw = unitBoundaries(tiers.middle, timelineStart, timelineEnd);
    return raw.map((seg, i) => {
      const x1 = dayToX(seg.start < timelineStart ? timelineStart : seg.start);
      const x2 = dayToX(seg.end > timelineEnd ? timelineEnd : seg.end);
      return {
        key: `m-${seg.start.getTime()}`,
        label: middleTierLabel(tiers.middle, seg.start),
        x: x1,
        width: Math.max(x2 - x1, 0),
        even: i % 2 === 0,
        // For day-unit, mark weekends for muted color
        isWknd: tiers.middle === 'day' && (seg.start.getDay() === 0 || seg.start.getDay() === 6),
      };
    });
  }, [tiers.middle, timelineStart, timelineEnd, dayToX]);

  // Vertical grid lines drawn at middle-tier boundaries (visual cadence)
  const gridLines = useMemo(() => middleTier.map(seg => seg.x), [middleTier]);

  // G-S4: weekend shading visible at Month and finer (pxPerDay ≥ 8)
  const weekendStripes = useMemo(() => {
    if (pxPerDay < 8) return [];
    const out = [];
    let cur = new Date(timelineStart); cur.setHours(0, 0, 0, 0);
    while (cur < timelineEnd) {
      const dow = cur.getDay();
      if (dow === 0 || dow === 6) out.push({ x: dayToX(cur), w: pxPerDay });
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [timelineStart, timelineEnd, pxPerDay, dayToX]);

  const depArrows = useMemo(() => {
    return visibleRows.flatMap((succ, si) => {
      if (!succ.predecessorSortOrder || !succ._start) return [];
      const pi = visibleRows.findIndex(r => r.sortOrder === succ.predecessorSortOrder);
      if (pi < 0 || !visibleRows[pi]._finish) return [];
      const pred = visibleRows[pi];
      const predEndX = dayToX(pred._finish);
      const succStartX = dayToX(succ._start);
      const midX = predEndX + 12;
      const predY = rowYOffset(pi) + rowH(pi) / 2;
      const succY = rowYOffset(si) + rowH(si) / 2;
      return [{ key: `${pred.id}-${succ.id}`, d: `M ${predEndX} ${predY} H ${midX} V ${succY} H ${succStartX}`, ax: succStartX, ay: succY }];
    });
  }, [visibleRows, dayToX, rowYOffset, rowH]);

  const todayX = dayToX(new Date());

  return (
    <div className="gantt-view">
      {/* ── Zoom toolbar (inline) ──────────────────────────────────── */}
      <div className="gantt-zoom-toolbar">
        <div className="gantt-zoom-group">
          {PRESETS.map(p => (
            <button
              key={p.label}
              className={`gantt-zoom-btn${zoomLabel === p.label ? ' active' : ''}`}
              onClick={() => zoomTo(p.px)}
              type="button"
            >{p.label}</button>
          ))}
        </div>
        <input
          className="gantt-zoom-slider"
          type="range"
          min={MIN_PX}
          max={MAX_PX}
          step={1}
          value={pxPerDay}
          onChange={e => zoomTo(Number(e.target.value))}
          aria-label="Zoom level (pixels per day)"
        />
        <span className="gantt-zoom-label">{Math.round(pxPerDay)} px/day</span>
        <button
          className="gantt-tb-btn"
          type="button"
          onClick={fitToProject}
          data-testid="gantt-fit"
          title="Fit entire project to viewport"
        >⊞ Fit to Project</button>
        <button
          className="gantt-tb-btn"
          type="button"
          onClick={scrollToToday}
          data-testid="gantt-today"
          title="Scroll timeline to today"
        >↑ Today</button>
        {/* VW-S3: shared collapse state — mirrors the Effort View buttons. */}
        <button
          className="gantt-tb-btn"
          type="button"
          onClick={onExpandAll}
          data-testid="gantt-expand-all"
          title="Expand every summary row"
        >Expand All</button>
        <button
          className="gantt-tb-btn"
          type="button"
          onClick={onCollapseAll}
          data-testid="gantt-collapse-all"
          title="Collapse every summary row"
        >Collapse All</button>
      </div>

      {/* ── Main container ─────────────────────────────────────────── */}
      <div className="gantt-wrapper">
        <div
          className="gantt-container"
          style={{ gridTemplateColumns: `${taskListWidth}px 6px 1fr` }}
        >

          {/* Left: fixed task list */}
          <div className="gantt-task-list" ref={taskListRef} onScroll={() => syncScroll('list')}>
            <div className="gantt-task-list-inner">
              <div className="gantt-task-thead" style={{ height: HEADER_HEIGHT }} role="row">
                {COLUMN_DEFS.map(col => (
                  <div
                    key={col.key}
                    className={`gantt-th gantt-th-${col.key}`}
                    style={{ width: colWidths[col.key] }}
                    role="columnheader"
                  >
                    <span className="gantt-th-label">{col.label}</span>
                    <div
                      className="gantt-col-resizer"
                      onMouseDown={e => startColResize(e, col.key)}
                      data-testid={`col-resizer-${col.key}`}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize column ${col.label}`}
                      tabIndex={0}
                    />
                  </div>
                ))}
              </div>
              {topSpacerH > 0 && (
                <div
                  data-testid="gantt-top-spacer"
                  style={{ height: topSpacerH, flexShrink: 0 }}
                  aria-hidden="true"
                />
              )}
              {visibleRows.slice(firstIdx, lastIdx + 1).map((row, idx) => {
                const i = firstIdx + idx;
                return (
                  <div
                    key={row.id}
                    ref={el => { rowRefs.current[i] = el; }}
                    className={`gantt-task-row${row.isSummaryDerived ? ' summary' : ''}${i % 2 ? ' alt' : ''}${hoveredIdx === i ? ' is-hovered' : ''}`}
                    data-testid={`gantt-row-${i}`}
                    onMouseEnter={() => setHoveredIdx(i)}
                    onMouseLeave={() => setHoveredIdx(prev => prev === i ? null : prev)}
                  >
                    <div className="gantt-cell gantt-cell-wbs" style={{ width: colWidths.wbs }}>{row.wbs || (i + 1)}</div>
                    <div className="gantt-cell gantt-cell-mode" style={{ width: colWidths.mode }}>
                      <span className={`mode-icon ${(row.taskMode || 'AUTO') === 'MANUAL' ? 'm' : 'a'}`}>
                        {(row.taskMode || 'AUTO') === 'MANUAL' ? 'M' : 'A'}
                      </span>
                    </div>
                    <div className="gantt-cell gantt-cell-name" style={{ width: colWidths.name }}>
                      <span className="gantt-name-inner" style={{ paddingLeft: row.outlineLevel * 16 }}>
                        {row.isSummaryDerived && (
                          <span
                            className="gantt-toggle"
                            onClick={() => toggleCollapse(row.id)}
                            role="button"
                            tabIndex={0}
                            aria-label={collapsed.has(row.id) ? 'Expand row' : 'Collapse row'}
                          >
                            {collapsed.has(row.id) ? '▸' : '▾'}
                          </span>
                        )}
                        {(row.isMilestone || row.durationDays === 0) && (
                          <span className="milestone-icon"></span>
                        )}
                        <span className="gantt-row-name">{row.name}</span>
                      </span>
                    </div>
                    <div className="gantt-cell gantt-cell-dur" style={{ width: colWidths.dur }}>{row.durationDays || 0}d</div>
                    <div className="gantt-cell gantt-cell-start" style={{ width: colWidths.start }}>{fmtD(row._start)}</div>
                    <div className="gantt-cell gantt-cell-finish" style={{ width: colWidths.finish }}>{fmtD(row._finish)}</div>
                  </div>
                );
              })}
              {bottomSpacerH > 0 && (
                <div
                  data-testid="gantt-bottom-spacer"
                  style={{ height: bottomSpacerH, flexShrink: 0 }}
                  aria-hidden="true"
                />
              )}
            </div>
          </div>

          {/* Splitter (G-S3) */}
          <div
            className="gantt-splitter"
            onMouseDown={startSplitterDrag}
            title="Drag to resize"
            data-testid="gantt-splitter"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize task and timeline panes"
            tabIndex={0}
          ></div>

          {/* Right: scrollable SVG timeline */}
          <div className="gantt-timeline" ref={timelineRef} onScroll={() => syncScroll('timeline')}>
            <svg width={totalWidth} height={totalHeight} style={{ display: 'block' }}>

              {/* Weekend shading */}
              {weekendStripes.map((s, i) => (
                <rect key={i} x={s.x} y={HEADER_HEIGHT} width={s.w} height={totalHeight - HEADER_HEIGHT} fill="#f3f3f3" />
              ))}

              {/* Alternating row backgrounds */}
              {visibleRows.slice(firstIdx, lastIdx + 1).map((_, idx) => {
                const i = firstIdx + idx;
                if (i % 2 !== 1) return null;
                return <rect key={`bg-${i}`} x={0} y={rowYOffset(i)} width={totalWidth} height={rowH(i)} fill="#f9f9f9" />;
              })}

              {/* G-S5: row hover layer — captures empty-row events for cross-pane sync */}
              {visibleRows.slice(firstIdx, lastIdx + 1).map((_, idx) => {
                const i = firstIdx + idx;
                return (
                  <rect
                    key={`hover-${i}`}
                    x={0}
                    y={rowYOffset(i)}
                    width={totalWidth}
                    height={rowH(i)}
                    fill={hoveredIdx === i ? 'rgba(74,144,217,0.10)' : 'transparent'}
                    pointerEvents="all"
                    onMouseEnter={() => setHoveredIdx(i)}
                    onMouseLeave={() => setHoveredIdx(prev => prev === i ? null : prev)}
                    data-testid={`gantt-svg-row-${i}`}
                  />
                );
              })}

              {/* Vertical grid lines at middle-tier boundaries */}
              {gridLines.map((x, i) => (
                <line key={`grid-${i}`} x1={x} y1={HEADER_HEIGHT} x2={x} y2={totalHeight}
                  stroke="#ebebeb" strokeWidth={1} />
              ))}

              {/* Today highlight stripe */}
              <rect x={todayX} y={HEADER_HEIGHT} width={Math.max(pxPerDay * 0.4, 1)} height={totalHeight - HEADER_HEIGHT} fill="rgba(229,57,53,0.07)" />

              {/* Header background */}
              <rect x={0} y={0} width={totalWidth} height={HEADER_HEIGHT} fill="#eceff1" />

              {/* Top tier */}
              {topTier.map((seg) => (
                <g key={seg.key}>
                  <rect x={seg.x} y={0} width={seg.width} height={TOP_TIER_HEIGHT}
                    fill={seg.even ? '#e3eaf3' : '#dde6f0'} />
                  <line x1={seg.x} y1={0} x2={seg.x} y2={TOP_TIER_HEIGHT} stroke="#c8d4e0" strokeWidth={1} />
                  <text x={seg.x + 6} y={19} fontSize={12} fontWeight="600" fill="#37474f">{seg.label}</text>
                </g>
              ))}

              {/* Middle tier */}
              {middleTier.map((seg) => (
                <g key={seg.key}>
                  <rect x={seg.x} y={TOP_TIER_HEIGHT} width={seg.width} height={MIDDLE_TIER_HEIGHT}
                    fill={seg.even ? '#eef2f7' : '#f3f6fa'} />
                  <line x1={seg.x} y1={TOP_TIER_HEIGHT} x2={seg.x} y2={HEADER_HEIGHT} stroke="#c8d4e0" strokeWidth={1} />
                  {seg.label && seg.width > 12 && (
                    <text x={seg.x + 4} y={47} fontSize={10}
                      fill={seg.isWknd ? '#b0bec5' : '#546e7a'}>{seg.label}</text>
                  )}
                </g>
              ))}

              {/* Header bottom separator */}
              <line x1={0} y1={HEADER_HEIGHT} x2={totalWidth} y2={HEADER_HEIGHT} stroke="#b0bec5" strokeWidth={1} />


              {/* Dependency arrows */}
              {depArrows.map(a => (
                <g key={a.key}>
                  <path d={a.d} fill="none" stroke="#90a4ae" strokeWidth={1.5} />
                  <polygon points={`${a.ax},${a.ay} ${a.ax - 6},${a.ay - 4} ${a.ax - 6},${a.ay + 4}`} fill="#90a4ae" />
                </g>
              ))}

              {/* Task bars */}
              {visibleRows.map((row, i) => {
                if (!row._start || !row._finish) return null;
                const x1 = dayToX(row._start);
                const x2 = dayToX(row._finish);
                const rH_i = rowH(i);
                const rowY = rowYOffset(i);
                const barTop = rowY + (rH_i - BAR_HEIGHT) / 2;
                const w = Math.max(x2 - x1, 2);
                const color = barColor(row);
                const handlers = {
                  onMouseEnter: e => onBarEnter(e, row, i),
                  onMouseLeave: onBarLeave,
                  onMouseMove: onBarMove,
                };

                if (row.isMilestone || row.durationDays === 0) {
                  const cx = x1; const cy = rowY + rH_i / 2;
                  return (
                    <polygon key={row.id} {...handlers} style={{ cursor: 'pointer' }}
                      points={`${cx},${cy - 9} ${cx + 9},${cy} ${cx},${cy + 9} ${cx - 9},${cy}`}
                      fill="#e65100" />
                  );
                }

                if (row.isSummaryDerived) {
                  // VW-S2: thicker, labeled summary bar — 14 px tall, centered in
                  // the bar slot, with classic MS-Project triangular end caps and
                  // an inline phase / sprint name when the bar is wide enough.
                  const summaryH = 14;
                  const by = barTop + (BAR_HEIGHT - summaryH) / 2;
                  return (
                    <g key={row.id} {...handlers} style={{ cursor: 'pointer' }}>
                      <rect x={x1} y={by} width={w} height={summaryH} rx={2} fill="#37474f" />
                      <polygon points={`${x1},${by + summaryH} ${x1},${by + summaryH + 6} ${x1 + 8},${by + summaryH}`} fill="#37474f" />
                      <polygon points={`${x1 + w},${by + summaryH} ${x1 + w},${by + summaryH + 6} ${x1 + w - 8},${by + summaryH}`} fill="#37474f" />
                      {w > 80 && (
                        <text x={x1 + 6} y={by + summaryH / 2 + 4} fill="#fff" fontSize="11" fontFamily="sans-serif" style={{ pointerEvents: 'none' }}>
                          {row.name}
                        </text>
                      )}
                    </g>
                  );
                }

                const pw = w * getProgress(row);
                const roleLabel = roles
                  .filter(r => row.roles?.[r.id]?.plannedHours > 0)
                  .map(r => `${r.abbreviation} ${row.roles[r.id].plannedHours}h`)
                  .join(' · ');
                return (
                  <g key={row.id} {...handlers} style={{ cursor: 'pointer' }}>
                    <rect x={x1} y={barTop} width={w} height={BAR_HEIGHT} rx={3} fill={color} opacity={0.8} />
                    {pw > 0 && <rect x={x1} y={barTop} width={pw} height={BAR_HEIGHT} rx={3} fill={color} />}
                    {w > 60 && roleLabel && (
                      <text x={x1 + 5} y={barTop + BAR_HEIGHT / 2 + 4} fontSize={10} fill="#fff" style={{ pointerEvents: 'none' }}>
                        {roleLabel}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Today line — rendered on top of everything */}
              <line x1={todayX} y1={0} x2={todayX} y2={totalHeight}
                stroke="#e53935" strokeWidth={2} strokeDasharray="4 4" />

              {/* G-S5: Today label badge above the line */}
              <g pointerEvents="none">
                <rect x={todayX - 22} y={2} width={44} height={14} rx={2} fill="#e53935" />
                <text
                  x={todayX}
                  y={12}
                  fontSize={9}
                  fontWeight={700}
                  fill="#fff"
                  textAnchor="middle"
                  data-testid="gantt-today-label"
                >Today</text>
              </g>

            </svg>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div className="gantt-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="gantt-tooltip-title">{tooltip.row.name}</div>
          <div>{fmtD(tooltip.row._start)} → {fmtD(tooltip.row._finish)}</div>
          {tooltip.row.durationDays > 0 && <div>Duration: {tooltip.row.durationDays}d</div>}
          {tooltip.row.status && <div>Status: {tooltip.row.status}</div>}
          {roles.filter(r => tooltip.row.roles?.[r.id]?.plannedHours > 0).map(r => (
            <div key={r.id}>{r.abbreviation}: {tooltip.row.roles[r.id].plannedHours}h</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GanttView;
